/**
 * Shows the authorized Vault lineage and supports explicit drag destinations.
 *
 * Breadcrumb IDs are navigation targets, not permission; the backend rechecks
 * every operation. The component keeps path context and drop intent visible so
 * moving content does not depend on hidden current-folder state.
 */
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/components/ui'
import type { VaultItem } from '../types'
import { getDragPayload, hasDragPayload, type VaultDragPayload } from '../lib/format'

interface VaultBreadcrumbsProps {
  items: VaultItem[]
  onNavigate: (folderId: string) => void
  onDropItem: (payload: VaultDragPayload, targetFolderId: string) => void
}

export function VaultBreadcrumbs({ items, onNavigate, onDropItem }: VaultBreadcrumbsProps) {
  if (items.length < 2) return null
  return (
    <nav aria-label="Vault breadcrumb" className="flex flex-wrap items-center gap-1 text-sm animate-in fade-in duration-200">
      {items.map((item, index) => (
        <span key={item.itemId} className="flex items-center gap-1">
          {index > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          <CrumbTarget
            label={item.name}
            current={index === items.length - 1}
            onNavigate={() => onNavigate(item.itemId)}
            onDropItem={(payload) => onDropItem(payload, item.itemId)}
          />
        </span>
      ))}
    </nav>
  )
}

interface CrumbTargetProps {
  label: string
  current: boolean
  onNavigate: () => void
  onDropItem: (payload: VaultDragPayload) => void
}

function CrumbTarget({ label, current, onNavigate, onDropItem }: CrumbTargetProps) {
  const [over, setOver] = useState(false)
  return (
    <button
      type="button"
      onClick={onNavigate}
      onDragOver={(event) => {
        if (current || !hasDragPayload(event)) return
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        setOver(false)
        const payload = getDragPayload(event)
        if (!current && payload) {
          event.preventDefault()
          onDropItem(payload)
        }
      }}
      className={cn(
        'rounded px-1 py-0.5 transition-colors hover:underline',
        current ? 'font-medium text-foreground' : 'text-muted-foreground',
        over && 'bg-primary/10 text-primary ring-1 ring-primary/50',
      )}
    >
      {label}
    </button>
  )
}
